/**
 * Deterministic Main Command Overseer AI-context policy (#450).
 * Separate from Worker PTY / #414: rotating Overseer chat context never
 * restarts a healthy Worker terminal.
 *
 * #688: an optional measured/estimated context load closes the gap where a same-task
 * follow-up kept reusing a context past its budget. When the #688 meter says the context is
 * due for rotation, the Overseer rotates at this turn boundary - even on a same-task
 * follow-up - and the durable context pack carries the task forward.
 */
import { contextRotationDue, type ContextLoadDecision } from '../context/context-meter.js';

export type OverseerContextStatus = 'fresh' | 'growing' | 'rotated';
export type OverseerContextAction = 'reuse' | 'rotate' | 'reuse_same_task';
export type OverseerContextReason =
  | 'within_budget'
  | 'same_task_follow_up'
  | 'turn_budget_exceeded'
  | 'stale'
  | 'unrelated_goal'
  | 'confused'
  | 'wrong_runtime'
  | 'manual'
  /** #688: the context meter says this context is at its rotation point. */
  | 'context_budget';

export interface OverseerContextPolicyLimits {
  readonly maxRecentTurns: number;
  readonly staleAfterMs: number;
  readonly growingAfterTurns: number;
}

export const DEFAULT_OVERSEER_CONTEXT_LIMITS: OverseerContextPolicyLimits = {
  maxRecentTurns: 12,
  staleAfterMs: 2 * 60 * 60 * 1000,
  growingAfterTurns: 6,
};

export interface OverseerContextSnapshot {
  readonly sessionEpoch: number;
  readonly turnCount: number;
  readonly lastActiveAtMs: number;
  readonly status: OverseerContextStatus;
  readonly runtimeKey: string | null;
  readonly taskChainId: string | null;
}

export interface IncomingOverseerTurn {
  readonly nowMs: number;
  readonly runtimeKey: string | null;
  readonly taskChainId: string | null;
  readonly sameTaskFollowUp: boolean;
  readonly unrelatedGoal: boolean;
  readonly confused: boolean;
  readonly manualRotate: boolean;
  /** #688: the context-load decision for the current context; absent = not measured. */
  readonly contextLoad?: ContextLoadDecision;
}

export interface OverseerContextDecision {
  readonly action: OverseerContextAction;
  readonly reason: OverseerContextReason;
  readonly nextStatus: OverseerContextStatus;
  readonly needsContextPack: boolean;
  readonly preservesWorkerPty: true;
}

export function decideOverseerContext(
  current: OverseerContextSnapshot,
  incoming: IncomingOverseerTurn,
  limits: OverseerContextPolicyLimits = DEFAULT_OVERSEER_CONTEXT_LIMITS,
): OverseerContextDecision {
  const preservesWorkerPty = true as const;

  if (incoming.manualRotate) {
    return { action: 'rotate', reason: 'manual', nextStatus: 'rotated', needsContextPack: true, preservesWorkerPty };
  }
  if (incoming.confused) {
    return { action: 'rotate', reason: 'confused', nextStatus: 'rotated', needsContextPack: true, preservesWorkerPty };
  }
  if (current.runtimeKey !== null && incoming.runtimeKey !== null && current.runtimeKey !== incoming.runtimeKey) {
    return { action: 'rotate', reason: 'wrong_runtime', nextStatus: 'rotated', needsContextPack: true, preservesWorkerPty };
  }
  // #688: a context at its rotation point rotates at this boundary whatever else is true,
  // including a same-task follow-up; the pack carries the task, so nothing is lost.
  if (incoming.contextLoad !== undefined && contextRotationDue(incoming.contextLoad)) {
    return { action: 'rotate', reason: 'context_budget', nextStatus: 'rotated', needsContextPack: true, preservesWorkerPty };
  }
  if (incoming.unrelatedGoal) {
    return { action: 'rotate', reason: 'unrelated_goal', nextStatus: 'rotated', needsContextPack: true, preservesWorkerPty };
  }
  // A same-task chain is exactly where unattended context can grow indefinitely. The turn
  // budget is a real Brunel boundary even when a token ceiling is still Unknown, so apply it
  // before the ordinary same-task reuse rule.
  if (current.turnCount >= limits.maxRecentTurns) {
    return { action: 'rotate', reason: 'turn_budget_exceeded', nextStatus: 'rotated', needsContextPack: true, preservesWorkerPty };
  }
  if (incoming.sameTaskFollowUp) {
    let nextStatus: OverseerContextStatus = current.status === 'rotated' ? 'fresh' : current.status;
    if (current.turnCount + 1 >= limits.growingAfterTurns) nextStatus = 'growing';
    if (incoming.contextLoad?.state === 'prepare_handoff' || incoming.contextLoad?.state === 'growing') nextStatus = 'growing';
    return {
      action: 'reuse_same_task',
      reason: 'same_task_follow_up',
      nextStatus,
      needsContextPack: false,
      preservesWorkerPty,
    };
  }
  if (current.lastActiveAtMs > 0 && incoming.nowMs - current.lastActiveAtMs >= limits.staleAfterMs) {
    return { action: 'rotate', reason: 'stale', nextStatus: 'rotated', needsContextPack: true, preservesWorkerPty };
  }

  const nextStatus: OverseerContextStatus =
    current.turnCount + 1 >= limits.growingAfterTurns ? 'growing' : 'fresh';
  return {
    action: 'reuse',
    reason: 'within_budget',
    nextStatus: current.status === 'rotated' ? 'fresh' : nextStatus,
    needsContextPack: current.status === 'rotated' || current.status === 'fresh',
    preservesWorkerPty,
  };
}

export function boundRecentTurns<T>(turns: readonly T[], maxRecentTurns: number): readonly T[] {
  const limit = Math.max(0, maxRecentTurns);
  if (turns.length <= limit) return turns;
  return turns.slice(turns.length - limit);
}
